import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import api from '../../services/api';
import '../../styles/components.css';

const TABS = [
    { key: 'scholars', label: 'Scholars' },
    { key: 'supervisors', label: 'Supervisors' },
    { key: 'dlc', label: 'DLC' },
    { key: 'hod', label: 'HOD' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Generic sortable table — no external sort state needed, fully self-contained
// columns: [{ header, field?, render?, numeric? }]
// ─────────────────────────────────────────────────────────────────────────────
const SortableTable = ({ columns, data, emptyMessage = 'No records found.' }) => {
    const [sortConfig, setSortConfig] = useState({ field: null, dir: null });

    const cycleSort = (field) => {
        if (!field) return;
        setSortConfig(prev => {
            if (prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return { field: null, dir: null };
        });
    };

    const sortIcon = (field) => {
        if (sortConfig.field !== field) return '⇅';
        return sortConfig.dir === 'asc' ? '▲' : '▼';
    };

    const sorted = React.useMemo(() => {
        const { field, dir } = sortConfig;
        if (!field || !dir) return data;
        return [...data].sort((a, b) => {
            const col = columns.find(c => c.field === field);
            const av = (a[field] ?? '');
            const bv = (b[field] ?? '');
            const cmp = col?.numeric
                ? (Number(av) || 0) - (Number(bv) || 0)
                : av.toString().toLowerCase().localeCompare(bv.toString().toLowerCase());
            return dir === 'asc' ? cmp : -cmp;
        });
    }, [data, sortConfig, columns]);

    const thStyle = (field) => ({
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#374151',
        background: '#f9fafb',
        borderBottom: '2px solid #e5e7eb',
        whiteSpace: 'nowrap',
        cursor: field ? 'pointer' : 'default',
        userSelect: 'none',
    });

    const tdStyle = {
        padding: '0.75rem 1rem',
        fontSize: '0.85rem',
        color: '#111827',
        borderBottom: '1px solid #f3f4f6',
        verticalAlign: 'middle',
    };

    if (!data.length) {
        return (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                {emptyMessage}
            </p>
        );
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                    <tr>
                        {columns.map((col, i) => (
                            <th
                                key={i}
                                style={thStyle(col.field)}
                                onClick={() => cycleSort(col.field)}
                                title={col.field ? `Sort by ${col.header}` : undefined}
                            >
                                {col.header}{col.field ? <>&nbsp;{sortIcon(col.field)}</> : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row, ri) => (
                        <tr
                            key={row.id || ri}
                            style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                            onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? '#fff' : '#fafafa'}
                        >
                            {columns.map((col, ci) => (
                                <td key={ci} style={tdStyle}>
                                    {col.render ? col.render(row) : (row[col.field] ?? '—')}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Search bar + clear button
// ─────────────────────────────────────────────────────────────────────────────
const SearchBar = ({ value, onChange, placeholder }) => (
    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
        <input
            type="text"
            className="form-input"
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{ flex: 1, minWidth: '240px' }}
        />
        {value && (
            <button
                onClick={() => onChange('')}
                style={{
                    padding: '0.5rem 0.85rem', background: '#e5e7eb',
                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                    fontSize: '0.875rem', color: '#374151',
                }}
            >
                ✕ Clear
            </button>
        )}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const UserManagement = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('scholars');
    const [scholars, setScholars] = useState([]);
    const [supervisors, setSupervisors] = useState([]);
    const [dlcUsers, setDlcUsers] = useState([]);
    const [hodUsers, setHodUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    // Per-tab search queries
    const [scholarSearch, setScholarSearch] = useState('');
    const [supervisorSearch, setSupervisorSearch] = useState('');
    const [dlcSearch, setDlcSearch] = useState('');
    const [hodSearch, setHodSearch] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [scholarsRes, supervisorsRes, dlcRes, hodRes, deptsRes] = await Promise.all([
                api.get('/admin/scholars'),
                api.get('/admin/supervisors'),
                api.get('/admin/dlc-users'),
                api.get('/admin/hod-users'),
                api.get('/admin/departments'),
            ]);
            setScholars(scholarsRes.data || []);
            setSupervisors(supervisorsRes.data || []);
            setDlcUsers(dlcRes.data || []);
            setHodUsers(hodRes.data || []);
            setDepartments(deptsRes.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // ── Scholar handlers ──────────────────────────────────────────────────────
    const handleEditScholar = (scholar) => {
        setEditingItem({ ...scholar, type: 'scholar' });
        let balCLExpireStr = '';
        if (scholar.balCLExpire) {
            const date = scholar.balCLExpire._seconds
                ? new Date(scholar.balCLExpire._seconds * 1000)
                : new Date(scholar.balCLExpire);
            if (!isNaN(date.getTime())) balCLExpireStr = date.toISOString().split('T')[0];
        }
        setFormData({
            scholarName: scholar.scholarName || '',
            tceRollNo: scholar.tceRollNo || '',
            email: scholar.email || '',
            phone: scholar.phone || '',
            department: scholar.department || '',
            supervisorName: scholar.supervisorName || '',
            fellowshipStatus: scholar.fellowshipStatus || 'pursuing',
            phdStatus: scholar.phdStatus || 'pursuing',
            balCL: scholar.balCL || 0,
            balCLExpire: balCLExpireStr,
            trfAdmittedDate: scholar.month || '',
            totalFellowshipReceived: scholar.totalFellowshipReceived || 0,
            publicationsJournal: scholar.publicationsJournal || 0,
            publicationsConference: scholar.publicationsConference || 0,
            publicationsBookChapter: scholar.publicationsBookChapter || 0,
            remarks: scholar.remarks || '',
        });
        setShowModal(true);
    };
    const handleSaveScholar = async () => {
        try {
            await api.put(`/admin/scholars/${editingItem.id}`, formData);
            alert('Scholar updated successfully');
            setShowModal(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to update scholar');
        }
    };
    const handleDeleteScholar = async (id) => {
        if (!window.confirm('Are you sure you want to delete this scholar?')) return;
        try {
            await api.delete(`/admin/scholars/${id}`);
            alert('Scholar deleted successfully');
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete scholar');
        }
    };

    // ── Supervisor handlers ───────────────────────────────────────────────────
    const handleEditSupervisor = (user) => {
        setEditingItem({ ...user, type: 'supervisor' });
        setFormData({
            fullName: user.fullName || '',
            email: user.email || '',
            department: user.department || '',
            isActive: user.isActive !== undefined ? user.isActive : true,
        });
        setShowModal(true);
    };
    const handleSaveSupervisor = async () => {
        try {
            await api.put(`/admin/supervisors/${editingItem.id}`, formData);
            alert('Supervisor updated successfully');
            setShowModal(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to update supervisor');
        }
    };
    const handleDeleteSupervisor = async (id) => {
        if (!window.confirm('Are you sure you want to delete this supervisor?')) return;
        try {
            await api.delete(`/admin/supervisors/${id}`);
            alert('Supervisor deleted successfully');
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete supervisor');
        }
    };

    // ── DLC handlers ──────────────────────────────────────────────────────────
    const handleEditDLC = (user) => {
        setEditingItem({ ...user, type: 'dlc' });
        setFormData({
            fullName: user.fullName || '',
            email: user.email || '',
            department: user.department || '',
            isActive: user.isActive !== undefined ? user.isActive : true,
        });
        setShowModal(true);
    };
    const handleSaveDLC = async () => {
        try {
            await api.put(`/admin/dlc-users/${editingItem.id}`, formData);
            alert('DLC user updated successfully');
            setShowModal(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to update DLC user');
        }
    };
    const handleDeleteDLC = async (id) => {
        if (!window.confirm('Are you sure you want to delete this DLC user?')) return;
        try {
            await api.delete(`/admin/dlc-users/${id}`);
            alert('DLC user deleted successfully');
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete DLC user');
        }
    };

    // ── HOD handlers ──────────────────────────────────────────────────────────
    const handleEditHOD = (user) => {
        setEditingItem({ ...user, type: 'hod' });
        setFormData({
            fullName: user.fullName || '',
            email: user.email || '',
            department: user.department || '',
            isActive: user.isActive !== undefined ? user.isActive : true,
        });
        setShowModal(true);
    };
    const handleSaveHOD = async () => {
        try {
            await api.put(`/admin/hod-users/${editingItem.id}`, formData);
            alert('HOD user updated successfully');
            setShowModal(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to update HOD user');
        }
    };
    const handleDeleteHOD = async (id) => {
        if (!window.confirm('Are you sure you want to delete this HOD user?')) return;
        try {
            await api.delete(`/admin/hod-users/${id}`);
            alert('HOD user deleted successfully');
            fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete HOD user');
        }
    };

    // ── Save dispatcher ───────────────────────────────────────────────────────
    const handleSave = () => {
        if (!editingItem) return;
        if (editingItem.type === 'scholar') return handleSaveScholar();
        if (editingItem.type === 'supervisor') return handleSaveSupervisor();
        if (editingItem.type === 'dlc') return handleSaveDLC();
        if (editingItem.type === 'hod') return handleSaveHOD();
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const statusBadge = (status) => {
        const styles = {
            pursuing: { background: '#dcfce7', color: '#166534' },
            active: { background: '#dcfce7', color: '#166534' },
            completed: { background: '#dbeafe', color: '#1e40af' },
            suspended: { background: '#fef3c7', color: '#92400e' },
            terminated: { background: '#fee2e2', color: '#991b1b' },
            relived: { background: '#ede9fe', color: '#5b21b6' },
            left: { background: '#f3f4f6', color: '#374151' },
        };
        const s = styles[status] || styles.pursuing;
        return (
            <span style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 500, ...s }}>
                {status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'}
            </span>
        );
    };

    const activeBadge = (isActive) => (
        <span style={{
            padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 500,
            background: isActive ? '#dcfce7' : '#fee2e2',
            color: isActive ? '#166534' : '#991b1b',
        }}>
            {isActive ? 'Active' : 'Inactive'}
        </span>
    );

    const actions = (onEdit, onDelete, row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="primary" size="small" onClick={() => onEdit(row)}>Edit</Button>
            <Button variant="danger" size="small" onClick={() => onDelete(row.id)}>Delete</Button>
        </div>
    );

    // ── Filtered data per tab ─────────────────────────────────────────────────
    const filterScholars = () => {
        const q = scholarSearch.toLowerCase();
        if (!q) return scholars;
        return scholars.filter(r =>
            (r.scholarName || '').toLowerCase().includes(q) ||
            (r.tceRollNo || '').toLowerCase().includes(q) ||
            (r.email || '').toLowerCase().includes(q) ||
            (r.department || '').toLowerCase().includes(q) ||
            (r.supervisorName || '').toLowerCase().includes(q) ||
            (r.fellowshipStatus || '').toLowerCase().includes(q) ||
            (r.phdStatus || '').toLowerCase().includes(q)
        );
    };

    const filterUsers = (list, q) => {
        if (!q) return list;
        const ql = q.toLowerCase();
        return list.filter(r =>
            (r.fullName || '').toLowerCase().includes(ql) ||
            (r.email || '').toLowerCase().includes(ql) ||
            (r.department || '').toLowerCase().includes(ql)
        );
    };

    // ── Column definitions ────────────────────────────────────────────────────
    const scholarColumns = [
        { header: 'Name', field: 'scholarName' },
        { header: 'TCE Roll No', field: 'tceRollNo' },
        { header: 'Email ID', field: 'email' },
        { header: 'Department', field: 'department' },
        { header: 'Supervisor', field: 'supervisorName' },
        { header: 'Fellowship Status', field: 'fellowshipStatus', render: r => statusBadge(r.fellowshipStatus) },
        {
            header: 'Ph.D. Status', field: 'phdStatus',
            render: r => (
                <span style={{
                    padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 500,
                    background: r.phdStatus === 'completed' ? '#dbeafe' : '#f3f4f6',
                    color: r.phdStatus === 'completed' ? '#1e40af' : '#374151',
                }}>
                    {r.phdStatus?.charAt(0).toUpperCase() + r.phdStatus?.slice(1) || 'Pursuing'}
                </span>
            )
        },
        { header: 'TRF Admitted', field: 'month' },
        { header: 'Actions', render: r => actions(handleEditScholar, handleDeleteScholar, r) },
    ];

    const supervisorColumns = [
        { header: 'Name', field: 'fullName' },
        { header: 'Email', field: 'email' },
        { header: 'Department', field: 'department' },
        { header: 'Status', field: 'isActive', render: r => activeBadge(r.isActive) },
        { header: 'Actions', render: r => actions(handleEditSupervisor, handleDeleteSupervisor, r) },
    ];

    const dlcColumns = [
        { header: 'Name', field: 'fullName' },
        { header: 'Email', field: 'email' },
        { header: 'Department', field: 'department' },
        { header: 'Status', field: 'isActive', render: r => activeBadge(r.isActive) },
        { header: 'Actions', render: r => actions(handleEditDLC, handleDeleteDLC, r) },
    ];

    const hodColumns = [
        { header: 'Name', field: 'fullName' },
        { header: 'Email', field: 'email' },
        { header: 'Department', field: 'department' },
        { header: 'Status', field: 'isActive', render: r => activeBadge(r.isActive) },
        { header: 'Actions', render: r => actions(handleEditHOD, handleDeleteHOD, r) },
    ];

    // ── Counts for tab labels ─────────────────────────────────────────────────
    const counts = {
        scholars: scholars.length,
        supervisors: supervisors.length,
        dlc: dlcUsers.length,
        hod: hodUsers.length,
    };

    if (loading) {
        return <Layout title="User Management"><div className="loading">Loading...</div></Layout>;
    }

    // ── Read-only email field (Supervisor / DLC / HOD) ────────────────────────
    const ReadOnlyEmail = () => (
        <div className="form-group">
            <label className="form-label">Email ID (Read-only)</label>
            <input
                type="email"
                className="form-input"
                value={formData.email || ''}
                readOnly
                style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
            />
        </div>
    );

    return (
        <Layout title="User Management">
            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                {TABS.map((tab, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === TABS.length - 1;
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                padding: '0.75rem 1.5rem',
                                border: 'none',
                                background: isActive ? '#3b82f6' : '#e5e7eb',
                                color: isActive ? 'white' : '#374151',
                                cursor: 'pointer',
                                borderRadius: isFirst ? '0.5rem 0 0 0.5rem' : isLast ? '0 0.5rem 0.5rem 0' : '0',
                                fontWeight: 500,
                                borderRight: !isLast ? '1px solid #d1d5db' : 'none',
                            }}
                        >
                            {tab.label} ({counts[tab.key]})
                        </button>
                    );
                })}
            </div>

            {/* Scholars Tab */}
            {activeTab === 'scholars' && (
                <Card title={`Enrolled Scholars (${filterScholars().length}${filterScholars().length !== scholars.length ? ` of ${scholars.length}` : ''})`}>
                    <SearchBar
                        value={scholarSearch}
                        onChange={setScholarSearch}
                        placeholder="Search by Name, TCE Roll No, Email, Department, Supervisor, Fellowship Status, Ph.D. Status..."
                    />
                    <SortableTable
                        columns={scholarColumns}
                        data={filterScholars()}
                        emptyMessage={scholarSearch ? 'No scholars match your search.' : 'No scholars enrolled yet.'}
                    />
                </Card>
            )}

            {/* Supervisors Tab */}
            {activeTab === 'supervisors' && (
                <Card title={`Supervisors (${filterUsers(supervisors, supervisorSearch).length}${filterUsers(supervisors, supervisorSearch).length !== supervisors.length ? ` of ${supervisors.length}` : ''})`}>
                    <SearchBar
                        value={supervisorSearch}
                        onChange={setSupervisorSearch}
                        placeholder="Search by Name, Email, Department..."
                    />
                    <SortableTable
                        columns={supervisorColumns}
                        data={filterUsers(supervisors, supervisorSearch)}
                        emptyMessage={supervisorSearch ? 'No supervisors match your search.' : 'No supervisors found.'}
                    />
                </Card>
            )}

            {/* DLC Tab */}
            {activeTab === 'dlc' && (
                <Card title={`DLC Users (${filterUsers(dlcUsers, dlcSearch).length}${filterUsers(dlcUsers, dlcSearch).length !== dlcUsers.length ? ` of ${dlcUsers.length}` : ''})`}>
                    <SearchBar
                        value={dlcSearch}
                        onChange={setDlcSearch}
                        placeholder="Search by Name, Email, Department..."
                    />
                    <SortableTable
                        columns={dlcColumns}
                        data={filterUsers(dlcUsers, dlcSearch)}
                        emptyMessage={dlcSearch ? 'No DLC users match your search.' : 'No DLC users found.'}
                    />
                </Card>
            )}

            {/* HOD Tab */}
            {activeTab === 'hod' && (
                <Card title={`HOD Users (${filterUsers(hodUsers, hodSearch).length}${filterUsers(hodUsers, hodSearch).length !== hodUsers.length ? ` of ${hodUsers.length}` : ''})`}>
                    <SearchBar
                        value={hodSearch}
                        onChange={setHodSearch}
                        placeholder="Search by Name, Email, Department..."
                    />
                    <SortableTable
                        columns={hodColumns}
                        data={filterUsers(hodUsers, hodSearch)}
                        emptyMessage={hodSearch ? 'No HOD users match your search.' : 'No HOD users found.'}
                    />
                </Card>
            )}

            {/* Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={`Edit ${editingItem?.type === 'scholar' ? 'Scholar' :
                    editingItem?.type === 'supervisor' ? 'Supervisor' :
                        editingItem?.type === 'dlc' ? 'DLC User' : 'HOD User'
                    }`}
                footer={
                    <>
                        <Button variant="primary" onClick={handleSave}>Save Changes</Button>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    </>
                }
            >
                {/* ── Scholar Edit Form ── */}
                {editingItem?.type === 'scholar' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                            <label className="form-label">Scholar Name</label>
                            <input type="text" className="form-input" value={formData.scholarName || ''} onChange={e => setFormData({ ...formData, scholarName: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">TCE Roll No</label>
                            <input type="text" className="form-input" value={formData.tceRollNo || ''} onChange={e => setFormData({ ...formData, tceRollNo: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Email ID (Read-only)</label>
                            <input type="email" className="form-input" value={formData.email || ''} readOnly style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Phone</label>
                            <input type="text" className="form-input" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Department</label>
                            <select className="form-select" value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value, supervisorName: '' })}>
                                <option value="">Select Department</option>
                                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Supervisor</label>
                            <select className="form-select" value={formData.supervisorName || ''} onChange={e => setFormData({ ...formData, supervisorName: e.target.value })}>
                                <option value="">Select Supervisor</option>
                                {supervisors.filter(s => s.department === formData.department).map(s => (
                                    <option key={s.id} value={s.fullName}>{s.fullName}</option>
                                ))}
                            </select>
                            {formData.department && supervisors.filter(s => s.department === formData.department).length === 0 && (
                                <small style={{ color: '#ef4444', display: 'block', marginTop: '0.25rem' }}>No supervisors found for this department.</small>
                            )}
                        </div>
                        <div className="form-group">
                            <label className="form-label">Fellowship Status</label>
                            <select className="form-select" value={formData.fellowshipStatus || 'pursuing'} onChange={e => setFormData({ ...formData, fellowshipStatus: e.target.value })}>
                                <option value="pursuing">Pursuing</option>
                                <option value="completed">Completed</option>
                                <option value="suspended">Suspended</option>
                                <option value="terminated">Terminated</option>
                                <option value="relived">Relived</option>
                                <option value="left">Left</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Ph.D. Status</label>
                            <select className="form-select" value={formData.phdStatus || 'pursuing'} onChange={e => setFormData({ ...formData, phdStatus: e.target.value })}>
                                <option value="pursuing">Pursuing</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Balance CL</label>
                            <input type="number" className="form-input" value={formData.balCL || 0} onChange={e => setFormData({ ...formData, balCL: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Balance CL Valid To</label>
                            <input type="date" className="form-input" value={formData.balCLExpire || ''} onChange={e => setFormData({ ...formData, balCLExpire: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">TRF Admitted Month &amp; Year</label>
                            <input type="text" className="form-input" placeholder="e.g., January, 2024" value={formData.trfAdmittedDate || ''} onChange={e => setFormData({ ...formData, trfAdmittedDate: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Total Fellowship Received (Rs)</label>
                            <input type="number" className="form-input" value={formData.totalFellowshipReceived || 0} onChange={e => setFormData({ ...formData, totalFellowshipReceived: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Publications (Journal)</label>
                            <input type="number" className="form-input" value={formData.publicationsJournal || 0} onChange={e => setFormData({ ...formData, publicationsJournal: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Publications (Conference)</label>
                            <input type="number" className="form-input" value={formData.publicationsConference || 0} onChange={e => setFormData({ ...formData, publicationsConference: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Publications (Book/Chapter)</label>
                            <input type="number" className="form-input" value={formData.publicationsBookChapter || 0} onChange={e => setFormData({ ...formData, publicationsBookChapter: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">Remarks</label>
                            <textarea className="form-input" rows="3" value={formData.remarks || ''} onChange={e => setFormData({ ...formData, remarks: e.target.value })} />
                        </div>
                    </div>
                )}

                {/* ── Supervisor Edit Form ── */}
                {editingItem?.type === 'supervisor' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                            <label className="form-label">Full Name (Read-only)</label>
                            <input type="text" className="form-input" value={formData.fullName || ''} readOnly style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                        </div>
                        <ReadOnlyEmail />
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">Department</label>
                            <select className="form-select" value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value })}>
                                <option value="">Select Department</option>
                                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" id="supIsActive" checked={!!formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} style={{ width: 'auto' }} />
                            <label htmlFor="supIsActive" className="form-label" style={{ margin: 0 }}>Active</label>
                        </div>
                    </div>
                )}

                {/* ── DLC Edit Form ── */}
                {editingItem?.type === 'dlc' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                            <label className="form-label">Full Name (Read-only)</label>
                            <input type="text" className="form-input" value={formData.fullName || ''} readOnly style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                        </div>
                        <ReadOnlyEmail />
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">Department</label>
                            <select className="form-select" value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value })}>
                                <option value="">Select Department</option>
                                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" id="dlcIsActive" checked={!!formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} style={{ width: 'auto' }} />
                            <label htmlFor="dlcIsActive" className="form-label" style={{ margin: 0 }}>Active</label>
                        </div>
                    </div>
                )}

                {/* ── HOD Edit Form ── */}
                {editingItem?.type === 'hod' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                            <label className="form-label">Full Name (Read-only)</label>
                            <input type="text" className="form-input" value={formData.fullName || ''} readOnly style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                        </div>
                        <ReadOnlyEmail />
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">Department</label>
                            <select className="form-select" value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value })}>
                                <option value="">Select Department</option>
                                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" id="hodIsActive" checked={!!formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} style={{ width: 'auto' }} />
                            <label htmlFor="hodIsActive" className="form-label" style={{ margin: 0 }}>Active</label>
                        </div>
                    </div>
                )}
            </Modal>
        </Layout>
    );
};

export default UserManagement;
